import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Grid,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import ImageUpload from './ImageUpload';

const ItemFormUnified = ({
  formData,
  onInputChange,
  onSubmit,
  isSubmitting,
  onCancel,
  channels = [],
  isEditMode = false,
  // Edit mode specific props
  onImageUpload,
  onImageDelete,
  onSetMainImage,
  // Create mode specific props
  bulkEntryMode,
  showForm,
  setShowForm,
  setBulkEntryMode,
  selectedChannelId,
  fetchNextItemNumber,
  onPrintLabel,
  labelRequiredChannelIds = new Set(),
  isLoadingImages = false,
}) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [nextItemNumber, setNextItemNumber] = useState(null);
  const [loadingItemNumber, setLoadingItemNumber] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Track original edit values to detect channel changes
  const originalEditRef = useRef(null);
  useEffect(() => {
    if (isEditMode && formData.salesChannelId && !originalEditRef.current) {
      originalEditRef.current = {
        salesChannelId: formData.salesChannelId,
        itemNumber: formData.itemNumber || null,
      };
    }
    if (!isEditMode) {
      originalEditRef.current = null;
    }
  }, [isEditMode, formData.salesChannelId, formData.itemNumber]);

  useEffect(() => {
    if (isEditMode && formData.images) {
      setExistingImages(formData.images);
    }
  }, [isEditMode, formData.images]);

  useEffect(() => {
    if (isEditMode) return;
    if (!selectedChannelId) return;
    if (formData.salesChannelId) return;

    onInputChange({
      target: {
        name: 'salesChannelId',
        value: selectedChannelId,
      },
    });
  }, [isEditMode, selectedChannelId, formData.salesChannelId, onInputChange]);

  // Auto-fill salesLocation from channel's defaultLocation
  useEffect(() => {
    if (!formData.salesChannelId) return;

    // In edit mode, only update when channel has changed from the original
    if (isEditMode) {
      const orig = originalEditRef.current;
      if (!orig || orig.salesChannelId === formData.salesChannelId) return;
    }

    const channel = channels.find((ch) => ch.id === formData.salesChannelId);
    if (channel?.defaultLocation) {
      onInputChange({
        target: { name: 'salesLocation', value: channel.defaultLocation },
      });
    }
    // channels and onInputChange excluded — effect should only fire when salesChannelId changes
  }, [isEditMode, formData.salesChannelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine if the selected channel uses custom item numbers
  const selectedChannel = channels.find((ch) => ch.id === formData.salesChannelId);
  const hasCustomItemNumbers = selectedChannel?.settings?.some(
    (s) => s.key === 'useCustomItemNumber' && s.value === true
  );

  // Fetch next item number preview for barcode-enabled channels
  // Create mode: always fetch when channel has barcodes
  // Edit mode: fetch when item lacks an itemNumber, or when channel has changed
  // channels excluded from deps — effect should only fire when salesChannelId or fetchNextItemNumber changes
  useEffect(() => {
    if (!fetchNextItemNumber) return;
    if (!formData.salesChannelId) {
      setNextItemNumber(null);
      return;
    }

    const channel = channels.find((ch) => ch.id === formData.salesChannelId);
    const hasItemNumbers = channel?.settings?.some(
      (s) => s.key === 'useItemNumber' && s.value === true
    );
    const usesCustom = channel?.settings?.some(
      (s) => s.key === 'useCustomItemNumber' && s.value === true
    );

    if (!hasItemNumbers || usesCustom) {
      setNextItemNumber(null);
      return;
    }

    if (isEditMode) {
      const orig = originalEditRef.current;
      const channelChanged = orig && orig.salesChannelId !== formData.salesChannelId;
      const missingItemNumber = !formData.itemNumber;

      // Only fetch if the item needs a new number:
      // - item has no itemNumber on an item-number channel (case 1)
      // - channel was changed (cases 2 & 3)
      if (!missingItemNumber && !channelChanged) return;
    }

    let cancelled = false;
    setLoadingItemNumber(true);
    fetchNextItemNumber(formData.salesChannelId).then((result) => {
      if (!cancelled) {
        setNextItemNumber(result);
        setLoadingItemNumber(false);
      }
    });
    return () => { cancelled = true; };
  }, [isEditMode, formData.salesChannelId, fetchNextItemNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImagesSelected = (files) => {
    setSelectedImages((prev) => [...prev, ...files]);
  };

  const handleDeleteImage = (imageToDelete) => {
    if (imageToDelete.storagePath) {
      // Existing image - use edit mode handler
      if (onImageDelete) {
        onImageDelete(imageToDelete);
        setExistingImages((prev) =>
          prev.filter((img) => img.storagePath !== imageToDelete.storagePath)
        );
      }
    } else {
      // New image - remove from selected
      setSelectedImages((prev) => prev.filter((img) => img !== imageToDelete));
    }
  };

  const handleSetMainImage = async (image) => {
    if (onSetMainImage && image.storagePath) {
      await onSetMainImage(image);
      setExistingImages((prev) =>
        prev.map((img) => ({
          ...img,
          isMainImage: img.url === image.url,
        }))
      );
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (isEditMode) {
      // Upload new images if any
      if (selectedImages.length > 0 && onImageUpload) {
        await onImageUpload(selectedImages);
        setSelectedImages([]);
      }
      onSubmit(e);
    } else {
      // Create mode - pass selected images
      onSubmit(e, selectedImages);
      setSelectedImages([]);
    }
  };

  const allImages = isEditMode ? [...existingImages, ...selectedImages] : selectedImages;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {isEditMode ? 'Edit Inventory Item' : 'Create New Inventory Item'}
        </Typography>
        {!isEditMode && (
          <FormControlLabel
            control={
              <Checkbox
                id="bulkEntryMode"
                name="bulkEntryMode"
                checked={bulkEntryMode}
                onChange={(e) => {
                  setBulkEntryMode(e.target.checked);
                }}
                size="small"
              />
            }
            label="Bulk Entry Mode"
            sx={{ mr: 0 }}
          />
        )}
      </Box>

      {/* Bulk Entry Mode Info - Create mode only */}
      {!isEditMode && bulkEntryMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Bulk Entry Mode is active. Form will stay open after each save.
        </Alert>
      )}

      <form onSubmit={handleFormSubmit}>
        <Stack spacing={3}>
          {/* Item Number (edit mode) */}
          {isEditMode && (() => {
            const orig = originalEditRef.current;
            const channelChanged = orig && orig.salesChannelId !== formData.salesChannelId;

            // Custom item number channel — show editable field
            if (hasCustomItemNumbers) {
              return (
                <TextField
                  fullWidth
                  label="Item Number"
                  name="itemNumber"
                  value={formData.itemNumber || ''}
                  onChange={onInputChange}
                  required
                  placeholder="Enter your item number"
                  variant="outlined"
                  helperText="Enter a unique item number for this item"
                  sx={{ maxWidth: 400 }}
                />
              );
            }

            // Channel changed or item has no number — show preview if available
            if (channelChanged || !formData.itemNumber) {
              if (loadingItemNumber) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading item number...</Typography>
                  </Box>
                );
              }
              if (nextItemNumber) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                    <Typography variant="body2" color="primary">
                      Next Item #: <strong>{nextItemNumber.itemNumber}</strong>
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (assigned on save)
                      </Typography>
                    </Typography>
                  </Box>
                );
              }
              return null;
            }

            // No change — show existing itemNumber as read-only
            if (formData.itemNumber) {
              return (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="Item Number"
                    value={formData.itemNumber}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    sx={{ maxWidth: 300, '& .MuiInputBase-root': { bgcolor: 'action.hover' } }}
                    helperText="Auto-assigned by the system"
                  />
                  {onPrintLabel && labelRequiredChannelIds.has(formData.salesChannelId) && (
                    <Tooltip title="Print Label">
                      <IconButton
                        size="small"
                        onClick={() => onPrintLabel({
                          itemNumber: formData.itemNumber,
                          itemTitle: formData.itemTitle,
                          listPrice: formData.listPrice,
                          salesChannelId: formData.salesChannelId,
                        })}
                        aria-label="Print Label"
                        sx={{ mt: 1 }}
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            }
            return null;
          })()}

          {/* Item Number (create mode) */}
          {!isEditMode && hasCustomItemNumbers && (
            <TextField
              fullWidth
              label="Item Number"
              name="itemNumber"
              value={formData.itemNumber || ''}
              onChange={onInputChange}
              required
              placeholder="Enter your item number"
              variant="outlined"
              helperText="Enter a unique item number for this item"
              sx={{ maxWidth: 400 }}
            />
          )}
          {!isEditMode && !hasCustomItemNumbers && (loadingItemNumber || nextItemNumber) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
              {loadingItemNumber ? (
                <CircularProgress size={16} />
              ) : nextItemNumber ? (
                <Typography variant="body2" color="primary">
                  Next Item #: <strong>{nextItemNumber.itemNumber}</strong>
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    (assigned on save)
                  </Typography>
                </Typography>
              ) : null}
            </Box>
          )}

          {/* Item Title */}
          <TextField
            fullWidth
            label="Title"
            name="itemTitle"
            value={formData.itemTitle}
            onChange={onInputChange}
            required
            placeholder="Enter item title"
            variant="outlined"
          />

          {/* Description */}
          <TextField
            fullWidth
            id="description"
            label="Description"
            name="description"
            value={formData.description}
            onChange={onInputChange}
            required
            placeholder="Enter item description"
            multiline
            rows={3}
            variant="outlined"
          />

          {/* Cost and List Price */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost ($)"
                name="cost"
                type="number"
                value={formData.cost}
                onChange={onInputChange}
                required
                inputProps={{ min: '0', step: '0.01' }}
                placeholder="0.00"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="List Price ($)"
                name="listPrice"
                type="number"
                value={formData.listPrice}
                onChange={onInputChange}
                required
                inputProps={{ min: '0', step: '0.01' }}
                placeholder="0.00"
                variant="outlined"
              />
            </Grid>
          </Grid>

          {/* Sales Channel and Location */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Sales Channel"
                name="salesChannelId"
                value={formData.salesChannelId || ''}
                onChange={onInputChange}
                required
                variant="outlined"
                InputLabelProps={{ htmlFor: undefined }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  },
                  sx: {
                    minWidth: '200px',
                  },
                }}
              >
                <MenuItem value="">Select Channel</MenuItem>
                {channels.map((ch) => (
                  <MenuItem key={ch.id} value={ch.id}>
                    {ch.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sales Location"
                name="salesLocation"
                value={formData.salesLocation}
                onChange={onInputChange}
                placeholder="Enter location (optional)"
                variant="outlined"
              />
            </Grid>
          </Grid>

          {/* Item Listing Link */}
          <TextField
            fullWidth
            label="Item Listing Link"
            name="itemLink"
            value={formData.itemLink || ''}
            onChange={onInputChange}
            placeholder="https://www.ebay.com/itm/..."
            variant="outlined"
          />

          {/* Next Item Number Preview (edit mode, channel changed to barcode-enabled) */}
          {isEditMode && (() => {
            const orig = originalEditRef.current;
            const channelChanged = orig && orig.salesChannelId !== formData.salesChannelId;
            const channel = channels.find((ch) => ch.id === formData.salesChannelId);
            const hasItemNumbers = channel?.settings?.some(
              (s) => s.key === 'useItemNumber' && s.value === true
            );
            // Only show here (below channel dropdown) if channel changed to non-item-number — show removal notice
            if (channelChanged && !hasItemNumbers && orig?.itemNumber) {
              return (
                <Box sx={{ pl: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Item number will be removed — new channel does not use item numbers.
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}

          {/* Item Quantity */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Quantity"
                name="itemQuantity"
                type="number"
                value={formData.itemQuantity}
                onChange={onInputChange}
                placeholder="Enter quantity (optional)"
                inputProps={{ min: '0', step: '1' }}
                variant="outlined"
              />
            </Grid>
          </Grid>

          {/* Image Upload Section */}
          <Accordion
            defaultExpanded={isEditMode && existingImages.length > 0}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {isEditMode ? `Images${existingImages.length > 0 ? ` (${existingImages.length})` : ''}` : 'Images (Optional)'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {isLoadingImages ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Loading images...</Typography>
                </Box>
              ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {isEditMode && existingImages.length > 0
                  ? 'Manage existing images or add new ones. Click an image to set it as the main image.'
                  : 'Upload images for this item. The first image will be set as the main image.'}
              </Typography>
              )}
              <ImageUpload
                images={allImages}
                onImagesSelected={handleImagesSelected}
                onDeleteImage={handleDeleteImage}
                onSetMainImage={isEditMode && existingImages.length > 0 ? handleSetMainImage : undefined}
                disabled={isSubmitting}
              />
            </AccordionDetails>
          </Accordion>

          {/* Validation Error */}
          {validationError && (
            <Alert severity="warning" onClose={() => setValidationError('')} sx={{ mb: 1 }}>{validationError}</Alert>
          )}

          {/* Form Actions */}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
            >
              {isSubmitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Creating...'
                : isEditMode
                ? 'Update Item'
                : bulkEntryMode
                ? 'Add Item'
                : 'Create Item'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={() => {
                if (!isEditMode && bulkEntryMode) {
                  setBulkEntryMode(false);
                } else if (!isEditMode && setShowForm) {
                  setShowForm(false);
                } else if (onCancel) {
                  onCancel();
                }
              }}
              startIcon={<CancelIcon />}
            >
              {!isEditMode && bulkEntryMode ? 'Exit Bulk' : 'Cancel'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default ItemFormUnified;
